import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Store, 
  ShoppingCart, 
  Package, 
  FileText, 
  DollarSign,
  Calendar,
  BarChart3,
  User,
  TrendingUp,
  Eye
} from 'lucide-react';
import api from '@/utils/api';
import { AdminPageSkeleton } from '@/components/admin/AdminPageSkeleton';
import { PageHeader } from '@/components/dashboard/shared/PageHeader';
import { KpiCard } from '@/components/dashboard/shared/KpiCard';
import { KpiGrid } from '@/components/dashboard/shared/KpiGrid';
import { dashboardStyles } from '@/components/dashboard/shared/dashboard-styles';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/currency';

interface PlatformOverview {
  totalUsers: number;
  totalStores: number;
  totalOrders: number;
  totalProducts: number;
  totalBlogs: number;
  activeStores: number;
  totalBookings: number;
  totalRevenue: number;
  revenueByCurrency: Array<{
    _id: string;
    totalRevenue: number;
    orderCount: number;
  }>;
  newUsersLast30Days: number;
  newOrdersLast30Days: number;
}

interface RecentUser {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
  role: string;
}

interface RecentOrder {
  _id: string;
  pricing: {
    total: number;
    currency: string;
  };
  status: string;
  createdAt: string;
  customer: {
    name: string;
    email: string;
  };
  storeId: {
    storeName: string;
    businessName: string;
    currency: string;
  };
}

interface AnalyticsData {
  overview: PlatformOverview;
  recent: {
    users: RecentUser[];
    orders: RecentOrder[];
  };
}

export function PlatformAnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/platform-analytics/overview');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'NGN') => {
    return formatPrice(amount, { currency });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'destructive';
      case 'owner':
        return 'default';
      case 'manager':
        return 'secondary';
      case 'customer':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return <AdminPageSkeleton kpiCount={4} />;
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform analytics"
        subtitle="Overview of platform performance and user activity"
        actions={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[180px]`}>
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchAnalytics} variant="outline" className="h-10 rounded-full px-4 text-sm font-semibold shadow-none">
              <BarChart3 className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        }
      />

      <KpiGrid skeletonCount={4}>
        <KpiCard
          label="Total users"
          value={data.overview.totalUsers}
          trend={`+${data.overview.newUsersLast30Days} this month`}
          icon={<Users />}
        />
        <KpiCard
          label="Total stores"
          value={data.overview.totalStores}
          trend={`${data.overview.activeStores} active`}
          icon={<Store />}
        />
        <KpiCard
          label="Total orders"
          value={data.overview.totalOrders}
          trend={`+${data.overview.newOrdersLast30Days} this month`}
          icon={<ShoppingCart />}
        />
        <KpiCard
          label="Total bookings"
          value={data.overview.totalBookings ?? 0}
          icon={<Calendar />}
        />
      </KpiGrid>

      <KpiGrid columns="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" skeletonCount={3}>
        <KpiCard label="Products" value={data.overview.totalProducts} icon={<Package />} />
        <KpiCard label="Blog posts" value={data.overview.totalBlogs} icon={<FileText />} />
        <KpiCard
          label="Revenue"
          value={
            data.overview.revenueByCurrency.length > 0
              ? data.overview.revenueByCurrency
                  .map((revenue) => formatCurrency(revenue.totalRevenue, revenue._id))
                  .join(' · ')
              : '—'
          }
          icon={<DollarSign />}
        />
      </KpiGrid>

      {/* Recent Activity */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <Card className={dashboardStyles.sectionCard}>
          <p className="text-sm font-heading font-semibold mb-3">Recent users</p>
          <div className="space-y-3">
            {data.recent.users.map((user) => (
              <div
                key={user._id}
                className="flex items-center justify-between p-2 bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors border border-transparent hover:border-muted-foreground/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center ring-1 ring-primary/10">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">
                      {user.name || 'Unnamed User'}
                    </div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </div>
                </div>
                <div className="text-right flex items-center gap-2">
                  <Badge variant={getRoleBadgeVariant(user.role)} className="text-xs">
                    {user.role}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(user.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className={dashboardStyles.sectionCard}>
          <p className="text-sm font-heading font-semibold mb-3">Recent orders</p>
          <div className="space-y-3">
            {data.recent.orders.map((order) => (
              <div
                key={order._id}
                className="flex items-center justify-between p-2 bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors border border-transparent hover:border-muted-foreground/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center ring-1 ring-primary/10">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">
                      {order.customer?.name || 'Unknown Customer'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {order.storeId?.storeName || 'Unknown Store'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-sm font-heading">
                    {formatCurrency(order.pricing.total, order.pricing.currency)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(order.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
